// The columns ShiftLens understands, and the header names that suggest each one.
// Vendor presets (T06) map onto these same fields.

export type FieldId =
  | 'name'
  | 'ref'
  | 'date'
  | 'start'
  | 'end'
  | 'break'
  | 'breakPaid'
  | 'dateOfBirth'
  | 'under18'
  | 'site'
  | 'optOut'
  | 'status'
  | 'kind';

export interface FieldDef {
  id: FieldId;
  /** Shown on the mapping screen. */
  label: string;
  hint?: string;
  /** Normalised header names (lowercase letters and digits only) that suggest this field. */
  synonyms: string[];
}

export const FIELDS: FieldDef[] = [
  {
    id: 'name',
    label: 'Name',
    synonyms: [
      'name',
      'employee',
      'employeename',
      'staff',
      'staffname',
      'staffmember',
      'fullname',
      'worker',
      'teammember',
      'person',
      'user',
      'username',
    ],
  },
  {
    id: 'ref',
    label: 'Staff ID',
    hint: 'A payroll or staff number. Used to match the same person across sites.',
    synonyms: [
      'id',
      'ref',
      'staffid',
      'employeeid',
      'employeeref',
      'employeenumber',
      'employeeno',
      'empid',
      'empno',
      'staffnumber',
      'staffno',
      'payrollnumber',
      'payrollid',
      'payrollno',
    ],
  },
  { id: 'date', label: 'Date', synonyms: ['date', 'shiftdate', 'day', 'workdate'] },
  {
    id: 'start',
    label: 'Start time',
    hint: 'A time, or a date and time.',
    synonyms: [
      'start',
      'starttime',
      'startdatetime',
      'starts',
      'startsat',
      'shiftstart',
      'from',
      'timein',
      'clockin',
      'begin',
    ],
  },
  {
    id: 'end',
    label: 'End time',
    hint: 'A time, or a date and time.',
    synonyms: [
      'end',
      'endtime',
      'enddatetime',
      'ends',
      'endsat',
      'shiftend',
      'finish',
      'finishtime',
      'to',
      'timeout',
      'clockout',
    ],
  },
  {
    id: 'break',
    label: 'Break length',
    hint: 'Minutes, or hours and minutes such as 0:30.',
    synonyms: [
      'break',
      'breaks',
      'breakmins',
      'breakminutes',
      'breaklength',
      'breakduration',
      'breaktime',
      'unpaidbreak',
      'unpaidbreakmins',
      'unpaidbreakminutes',
      'paidbreak',
      'paidbreakmins',
      'paidbreakminutes',
      'breakhrs',
      'breakhours',
    ],
  },
  { id: 'breakPaid', label: 'Break paid?', synonyms: ['breakpaid', 'paidbreakyn', 'ispaidbreak'] },
  {
    id: 'dateOfBirth',
    label: 'Date of birth',
    synonyms: ['dob', 'dateofbirth', 'birthdate', 'birthday'],
  },
  {
    id: 'under18',
    label: 'Under 18?',
    synonyms: ['under18', 'u18', 'youngworker', 'minor', 'isunder18'],
  },
  {
    id: 'site',
    label: 'Site',
    synonyms: ['site', 'location', 'branch', 'store', 'venue', 'shop', 'sitename', 'locationname'],
  },
  {
    id: 'optOut',
    label: '48-hour opt-out',
    synonyms: ['optout', '48hoptout', '48houroptout', '48hrsoptout', 'wtdoptout', 'optedout'],
  },
  { id: 'status', label: 'Shift status', synonyms: ['status', 'shiftstatus', 'state'] },
  { id: 'kind', label: 'Shift type', synonyms: ['shifttype', 'kind'] },
];

export const FIELD_BY_ID: Record<FieldId, FieldDef> = Object.fromEntries(
  FIELDS.map((f) => [f.id, f]),
) as Record<FieldId, FieldDef>;

/** Lowercases and strips everything but letters and digits: "Break (mins)" → "breakmins". */
export function normaliseHeader(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Column index for each mapped field. Unmapped fields are absent. */
export type ColumnMapping = Partial<Record<FieldId, number>>;

export type DateOrder = 'dmy' | 'mdy';

export interface MappingOptions {
  /** How to read ambiguous dates like 03/04/2026. Default day-first (UK), Q-T8. */
  dateOrder: DateOrder;
  /** Whether a plain number in the break column is minutes or hours. */
  breakUnit: 'minutes' | 'hours';
  /** Paid status implied by the break column's header ("Unpaid break" → false). */
  breakPaidFromHeader?: boolean;
}
